import { differenceInDays } from 'date-fns'
import { CircleAlert, CircleCheck, Star } from 'lucide-react'

import { toggleColumns } from '@fortem-monorepo/react-ag'
import { ICG2Rows, ICGG2Tables } from '@fortem-monorepo/web-sdk'

import {
  DataGridBlockOverrides,
  dataGridOverride,
} from '@/components/content-blocks/blocks/data-grid/types'
import { formatNumber } from '@/utilities/format'

export type IcGuidelinesTableId = ICGG2Tables.IcGuidelinesTableId

export const IC_GUIDELINES_DATA_GRID_OVERRIDES = {
  'summary.headline': dataGridOverride<ICG2Rows.SummaryHeadlineRow>(() => ({
    columnDefs: [
      { field: 'description' },
      {
        field: 'value',
        cellStyle: { textAlign: 'right' },
        valueFormatter: (params) => {
          const num = params.value
          if (typeof num !== 'number') return num

          const unitFormatter = () => {
            return formatNumber(num, { decimalPlaces: 3 })
          }

          const pctFormatter = () => {
            return formatNumber(num * 100, { decimalPlaces: 2, suffix: '%' })
          }

          const scoreFormatter = () => {
            return formatNumber(num, { decimalPlaces: 0 })
          }

          switch (params.data?.unit) {
            case 'unit':
              return unitFormatter()
            case 'pct':
              return pctFormatter()
            case 'score':
              return scoreFormatter()
            default:
              return unitFormatter()
          }
        },
      },
    ],
  })),
  'summary.cross_holdings': dataGridOverride<ICG2Rows.SummaryCrossHoldingsRow>(() => ({
    columnDefs: [
      { field: 'ucits_issuer' },
      { field: 'cis_issue_proportion_over_nav_pct' },
      { field: 'issue_proportion_of_cis_nav_pct' },
    ],
  })),
  'summary.ccy_exposures': dataGridOverride<ICG2Rows.SummaryCurrencyExposuresRow>(() => ({
    gridProps: {
      autoGroupColumnDef: { headerName: 'Currency' },
      isGroupOpenByDefault: () => false,
      suppressAggFuncInHeader: true,
      onColumnGroupOpened: (e) => e.api.autoSizeAllColumns(),
      onRowGroupOpened: (e) => {
        toggleColumns({
          event: e,
          columns: ['unique_identifier'],
          rowGroups: ['currency'],
        })
      },
    },
    columnDefs: [
      { field: 'unique_identifier', hide: true },
      { field: 'fund_name', hide: true },
      { field: 'currency_id', hide: true },
      { field: 'instrument_id', hide: true },
      {
        headerName: '',
        openByDefault: false,
        children: [
          { field: 'currency', rowGroup: true, hide: true },
          { field: 'All', columnGroupShow: 'open', aggFunc: 'sum' },
          { field: 'income', columnGroupShow: 'open', aggFunc: 'sum' },
          { field: 'beta', columnGroupShow: 'open', aggFunc: 'sum' },
          { field: 'diversifier', columnGroupShow: 'open', aggFunc: 'sum' },
          { field: 'cis', columnGroupShow: 'open', aggFunc: 'sum' },
          { field: 'protection', columnGroupShow: 'open', aggFunc: 'sum' },
          { field: 'cash_balance', columnGroupShow: 'open', aggFunc: 'sum' },
          { field: 'shareclass_hedging', columnGroupShow: 'open', aggFunc: 'sum' },
        ],
      },
    ],
  })),
  'summary.mtm': dataGridOverride<ICG2Rows.SummaryMtmRow>(() => ({
    columnDefs: [
      { field: 'time_bump' },
      { field: '-0.2', valueGetter: (params) => params.data?.['-0.2'] },
      { field: '-0.15', valueGetter: (params) => params.data?.['-0.15'] },
      { field: '-0.1', valueGetter: (params) => params.data?.['-0.1'] },
      { field: '-0.05', valueGetter: (params) => params.data?.['-0.05'] },
      { field: '0.0', valueGetter: (params) => params.data?.['0.0'] },
      { field: '0.05', valueGetter: (params) => params.data?.['0.05'] },
      { field: '0.1', valueGetter: (params) => params.data?.['0.1'] },
    ],
  })),
  'income.ucits': dataGridOverride<ICG2Rows.IncomeUcitsRow>(() => ({
    // @ts-ignore
    columnDefs: [
      { field: 'fund_name', hide: true },
      { field: 'value_date', hide: true },
      {
        field: 'ucits_clearance',
        rowGroup: true,
        hide: true,
        width: 200,
        cellRenderer: (params: any) => {
          const isOverallValid = params.node?.aggData['is_overall_valid']
          return (
            <div className={'flex gap-2 items-center shrink-0! w-fit!'}>
              {isOverallValid ? (
                <CircleCheck className={'text-green-600'} size={16} />
              ) : (
                <CircleAlert className={'text-destructive'} size={16} />
              )}
              {params.value}
            </div>
          )
        },
      },
      // @ts-ignore
      {
        field: 'ucits_issuer',
        rowGroup: true,
        hide: true,
        cellRenderer: (params: any) => {
          const isSpecialSovereignCase = params.node?.aggData['is_special_sovereign_case']
          const isOverallValid = params.node?.aggData['is_overall_valid']
          return (
            <div className={'flex gap-2 items-center shrink-0! w-fit!'}>
              {isOverallValid ? (
                <CircleCheck className={'text-green-600'} size={16} />
              ) : (
                <CircleAlert className={'text-destructive'} size={16} />
              )}
              {isSpecialSovereignCase && <Star size={16} fill="blue" stroke="blue" />}
              {params.value}
            </div>
          )
        },
      },
      { field: 'unique_identifier', hide: true },
      { field: 'short_name', hide: true },
      // @ts-ignore
      {
        field: 'is_limit_10_per_issue_good',
        headerName: '10%',
        cellClassRules: {
          ['bg-red-200']: (params) => {
            const sourceValue = !!!params.node.group && !params.value
            const groupValue =
              params.node.sourceRowIndex === -1 &&
              !!params.node?.aggData?.['is_limit_10_per_issue_good']
            return sourceValue || groupValue
          },
          ['bg-green-200']: (params) => {
            const sourceValue = !!!params.node.group && params.value
            const groupValue =
              params.node.sourceRowIndex === -1 &&
              !!!params.node?.aggData?.['is_limit_10_per_issue_good']
            return sourceValue || groupValue
          },
        },
        cellRenderer: () => {
          return <></>
        },
        aggFunc: (params) => {
          if (params.rowNode.childrenAfterGroup?.every((child) => child.sourceRowIndex === -1)) {
            return params.values?.some((value) => value)
          }
          return params.values?.some((value) => !value)
        },
      },
      // @ts-ignore
      {
        field: 'is_limit_5_40_good',
        headerName: '5/40%',
        cellClassRules: {
          ['bg-red-200']: (params) => {
            const sourceValue = !!!params.node.group && !params.value
            const isGroupValue =
              !!params.node.group && !!params.node?.aggData?.['is_limit_5_40_good']
            return sourceValue || isGroupValue
          },
          ['bg-green-200']: (params) => {
            const sourceValue = !!!params.node.group && params.value
            const isGroupValue =
              !!params.node.group && !!!params.node?.aggData?.['is_limit_5_40_good']
            return sourceValue || isGroupValue
          },
        },
        cellRenderer: () => {
          return <></>
        },
        aggFunc: (params) => {
          if (params.rowNode.childrenAfterGroup?.every((child) => child.sourceRowIndex === -1)) {
            return params.values?.some((value) => value)
          }
          return params.values?.some((value) => !value)
        },
      },
      // @ts-ignore
      {
        field: 'is_6_or_more_issues_good',
        headerName: '6',
        cellClassRules: {
          ['bg-red-200']: (params) => {
            const sourceValue = !!!params.node.group && !params.value
            const isGroupValue =
              !!params.node.group && !!params.node?.aggData?.['is_6_or_more_issues_good']
            return sourceValue || isGroupValue
          },
          ['bg-green-200']: (params) => {
            const sourceValue = !!!params.node.group && params.value
            const isGroupValue =
              !!params.node.group && !!!params.node?.aggData?.['is_6_or_more_issues_good']
            return sourceValue || isGroupValue
          },
        },
        cellRenderer: () => {
          return <></>
        },
        aggFunc: (params) => {
          if (params.rowNode.childrenAfterGroup?.every((child) => child.sourceRowIndex === -1)) {
            return params.values?.some((value) => value)
          }
          return params.values?.some((value) => !value)
        },
      },
      // @ts-ignore
      {
        field: 'is_max_single_issue_good',
        headerName: '30%',
        cellClassRules: {
          ['bg-red-200']: (params) => {
            const sourceValue = !!!params.node.group && !params.value
            const isGroupValue =
              !!params.node.group && !!params.node?.aggData?.['is_max_single_issue_good']
            return sourceValue || isGroupValue
          },
          ['bg-green-200']: (params) => {
            const sourceValue = !!!params.node.group && params.value
            const isGroupValue =
              !!params.node.group && !!!params.node?.aggData?.['is_max_single_issue_good']
            return sourceValue || isGroupValue
          },
        },
        cellRenderer: () => {
          return <></>
        },
        aggFunc: (params) => {
          if (params.rowNode.childrenAfterGroup?.every((child) => child.sourceRowIndex === -1)) {
            return params.values?.some((value) => value)
          }
          return params.values?.some((value) => !value)
        },
      },
      // @ts-ignore
      {
        field: 'is_limit_per_issuer_good',
        headerName: 'At Issuer',
        cellClassRules: {
          ['bg-red-200']: (params) => {
            const sourceValue = !!!params.node.group && !params.value
            const isGroupValue =
              !!params.node.group && !!params.node?.aggData?.['is_limit_per_issuer_good']
            return sourceValue || isGroupValue
          },
          ['bg-green-200']: (params) => {
            const sourceValue = !!!params.node.group && params.value
            const isGroupValue =
              !!params.node.group && !!!params.node?.aggData?.['is_limit_per_issuer_good']
            return sourceValue || isGroupValue
          },
        },
        cellRenderer: () => {
          return <></>
        },
        aggFunc: (params) => {
          if (params.rowNode.childrenAfterGroup?.every((child) => child.sourceRowIndex === -1)) {
            return params.values?.some((value) => value)
          }
          return params.values?.some((value) => !value)
        },
      },
      {
        field: 'is_special_sovereign_case',
        hide: true,
        aggFunc: (params) => {
          return params.values.every((value) => value)
        },
      },
      {
        field: 'is_overall_valid',
        hide: true,
        aggFunc: (params) => {
          return params.values.every((value) => value)
        },
      },
      // @ts-ignore
      {
        field: 'maturity_date',
        headerName: 'Maturity',
        cellClassRules: {
          ['bg-amber-200']: (params) => {
            const sourceValue =
              typeof params.value === 'string' && checkIncomeMaturityDate(new Date(params.value))
            const isGroupValue = typeof params.value === 'boolean' && params.value
            return sourceValue || isGroupValue
          },
        },
        cellRenderer: (params: any) => {
          if (params.node.aggData && params.value === true) {
            return <></>
          }

          if (typeof params.value === 'string') {
            return <>{params.value.toString()}</>
          }
        },
        aggFunc: (params) => {
          return params.values?.some((maturityDate) =>
            checkIncomeMaturityDate(new Date(maturityDate)),
          )
        },
      },
    ],
    gridProps: {
      autoGroupColumnDef: {
        headerName: 'Clearance → Issuer',
      },
      suppressAggFuncInHeader: true,
      autoSizeStrategy: {
        type: 'fitCellContents',
      },
      groupDefaultExpanded: 0,
      onRowGroupOpened: (event) => {
        const columns = ['unique_identifier', 'short_name']

        const numberOfOpenRows = event.api
          .getState()
          .rowGroupExpansion?.expandedRowGroupIds.filter((id) => id.includes('ucits_issuer')).length

        if (event.expanded && numberOfOpenRows! >= 0) {
          event.api.setColumnsVisible(columns, true)
        } else {
          numberOfOpenRows === 1 && event.api.setColumnsVisible(columns, false)
        }

        setTimeout(() => {
          event.api.autoSizeColumns(
            event.api
              .getAllGridColumns()
              .filter((col) => Object.values(col.getColDef()).length > 0),
          )
        }, 50)
      },
    },
  })),
  'beta.top_level': dataGridOverride<ICG2Rows.BetaTopLevelRow>(() => ({
    columnDefs: [
      { field: 'region_wrapper', rowGroup: true, hide: true },
      { field: 'under_unique_identifier', rowGroup: true, hide: true },
      { field: 'unique_identifier', hide: true },
      { field: 'position_delta_risk_weight_pct', aggFunc: 'sum' },
    ],
    gridProps: {
      groupDisplayType: 'multipleColumns',
      suppressAggFuncInHeader: true,
      isGroupOpenByDefault: () => false,
      onRowGroupOpened: function (event) {
        toggleColumns<ICG2Rows.BetaTopLevelRow>({
          event,
          columns: ['unique_identifier'],
          rowGroups: ['region_wrapper', 'under_unique_identifier'],
        })
      },
    },
  })),
  'diversifiers.strategic': dataGridOverride<ICG2Rows.DiversifiersStrategicRow>(() => ({
    columnDefs: [
      { field: 'unique_identifier' },
      {
        field: '1w_normalised_variance_contribution_1y',
        valueFormatter: ({ value }) => percentageFormatter(value),
      },
      {
        field: '1w_normalised_variance_contribution_3y',
        valueFormatter: ({ value }) => percentageFormatter(value),
      },
      {
        field: '1d_weighted_volatility_3y',
        valueFormatter: ({ value }) => percentageFormatter(value),
      },
      {
        field: '1w_correlation_vs_benchmark_3y',
        valueFormatter: ({ value }) => percentageFormatter(value),
      },
      { field: 'asset_class' },
      { field: 'factor' },
      { field: 'sub_factor' },
    ],
  })),
  'diversifiers.factor_weights': dataGridOverride<ICG2Rows.DiversifiersFactorWeightsRow>(() => ({
    columnDefs: [
      { field: 'asset_class' },
      { field: 'factor' },
      { field: 'sub_factor' },
      { field: '1w_normalised_variance_contribution_1y', valueFormatter: ({ value }) => percentageFormatter(value) },
      { field: '1w_normalised_variance_contribution_3y', valueFormatter: ({ value }) => percentageFormatter(value) },
      { field: 'position_delta_risk_weight_pct' },
    ],
  })),
  'diversifiers.gold_exposure': dataGridOverride<ICG2Rows.DiversifiersGoldExposureRow>(() => ({
    columnDefs: [
      { field: 'under_instrument_id' },
      { field: 'under_currency' },
      { field: 'exposure_lcl' },
      { field: 'rate' },
      { field: 'exposure_base' },
      { field: 'base_exposure_pct_of_total' },
      { field: 'per_issue_over_nav_pct' },
      { field: 'nav' },
    ],
  })),
  'protection.top_level': dataGridOverride<ICG2Rows.ProtectionTopLevelRow>(() => ({
    columnDefs: [
      { field: 'unique_identifier' },
      {
        field: '1w_normalised_variance_contribution_1y',
        valueFormatter: ({ value }) => percentageFormatter(value),
      },
      {
        field: '1w_normalised_variance_contribution_3y',
        valueFormatter: ({ value }) => percentageFormatter(value),
      },
      {
        field: '1d_weighted_volatility_3y',
        valueFormatter: ({ value }) => percentageFormatter(value),
      },
      {
        field: '1w_correlation_vs_benchmark_3y',
        valueFormatter: ({ value }) => percentageFormatter(value),
      },
      { field: 'asset_class' },
      { field: 'factor' },
      { field: 'sub_factor' },
      { field: 'spread_pct' },
    ],
  })),
  'protection.breakdown': dataGridOverride<ICG2Rows.ProtectionBreakdownRow>(() => ({
    gridProps: { autoGroupColumnDef: { headerName: 'Asset Class' } },
    columnDefs: [
      { field: 'unique_identifier' },
      { field: 'instrument_type' },
      { field: 'under_unique_identifier' },
      { field: 'under_short_name' },
      { field: 'asset_class', rowGroup: true, hide: true },
      { field: 'weight_pct' },
      { field: 'delta_to_underlyings_pct' },
      { field: 'position_delta_risk_weight_pct' },
      { field: 'maturity_date' },
    ],
  })),
  'protection.cost_over_nav': dataGridOverride<ICG2Rows.ProtectionCostOverNavRow>(() => ({
    transformRows(rows) {
      if (rows?.length) {
        return rows.map((o) => ({ ...o, top_group: 'Cost Over Nav' }))
      }
      return rows
    },
    gridProps: {
      isGroupOpenByDefault: () => false,
      suppressAggFuncInHeader: true,
      autoGroupColumnDef: { minWidth: 300 },
      onRowGroupOpened: (event) => {
        toggleColumns<ICG2Rows.ProtectionCostOverNavRow>({
          event,
          columns: [
            'structurename',
            'trade_price',
            'quantity',
            'unique_identifier',
            'trade_date',
            'trade_type',
          ],
          // @ts-ignore
          rowGroups: ['top_group', 'reference_unique_identifier'],
        })
      },
    },
    columnDefs: [
      // @ts-ignore
      { field: 'top_group', rowGroup: true, hide: true },
      { field: 'reference_unique_identifier', rowGroup: true, hide: true },
      { field: 'unique_identifier', hide: true },
      { field: 'structurename', hide: true },
      { field: 'cost_at_time_over_nav_pct', aggFunc: 'sum' },
      { field: 'trade_date', hide: true },
      { field: 'trade_type', hide: true },
    ],
  })),
} satisfies DataGridBlockOverrides<ICGG2Tables.IcGuidelinesTableId>

const checkIncomeMaturityDate = (maturityDate: Date) => {
  const maturityThresholdInDays = 365 * 2.5
  const _differenceInYears = differenceInDays(maturityDate, new Date())
  return _differenceInYears > maturityThresholdInDays
}

const percentageFormatter = (value: number) => {
  return formatNumber(value * 100, { suffix: '%', decimalPlaces: 2, roundingMode: 'round' })
}
